
// Function to detect trend reversal points
export const findTrendReversalPoints = (data) => {
    const reversalPoints = [];
    const lookbackPeriod = 3;  // Number of candles to look before and after
    const minPriceChange = 0.008;  // 0.8% minimum price change required

    // Calculate date threshold for 1 year ago
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    for (let i = lookbackPeriod; i < data.length - lookbackPeriod; i++) {
        const currentPoint = data[i];
        let isTop = false;
        let isBottom = false;

        // --- Top (Down Arrow) Detection ---
        // Condition 1: Is it a peak compared to the candles BEFORE it?
        const isPeakBefore = data.slice(i - lookbackPeriod, i).every(p => p.high <= currentPoint.high);
        // Condition 2: Is it a peak compared to the candles AFTER it?
        const isPeakAfter = data.slice(i + 1, i + 1 + lookbackPeriod).every(p => p.high <= currentPoint.high);
        // Condition 3: Is there a confirmed downtrend starting? (at least 2 falling candles)
        const hasConfirmedDowntrend = data[i + 1]?.low < currentPoint.low && data[i + 2]?.low < data[i + 1]?.low;

        if ((isPeakBefore || isPeakAfter) && hasConfirmedDowntrend) {
            isTop = true;
        }

        // --- Bottom (Up Arrow) Detection ---
        // Condition 1: Is it a trough compared to the candles BEFORE it?
        const isTroughBefore = data.slice(i - lookbackPeriod, i).every(p => p.low >= currentPoint.low);
        // Condition 2: Is it a trough compared to the candles AFTER it?
        const isTroughAfter = data.slice(i + 1, i + 1 + lookbackPeriod).every(p => p.low >= currentPoint.low);
        // Condition 3: Is there a confirmed uptrend starting? (at least 2 rising candles)
        const hasConfirmedUptrend = data[i + 1]?.high > currentPoint.high && data[i + 2]?.high > data[i + 1]?.high;

        if ((isTroughBefore || isTroughAfter) && hasConfirmedUptrend) {
            isBottom = true;
        }

        // Ensure a point is not both a top and a bottom
        if (isTop && isBottom) {
            continue; // Skip ambiguous points
        }

        // Calculate price change and add points if significant
        if (isTop || isBottom) {
            let priceChangePercent;
            if (isTop) {
                const futureMin = Math.min(...data.slice(i + 1, Math.min(i + lookbackPeriod + 1, data.length)).map(d => d.low));
                priceChangePercent = (currentPoint.high - futureMin) / currentPoint.high;
            } else { // isBottom
                const futureMax = Math.max(...data.slice(i + 1, Math.min(i + lookbackPeriod + 1, data.length)).map(d => d.high));
                priceChangePercent = (futureMax - currentPoint.low) / currentPoint.low;
            }

            if (priceChangePercent >= minPriceChange) {
                const pointDate = new Date(currentPoint.timestamp);
                if (pointDate >= oneYearAgo) {
                    reversalPoints.push({
                        index: i,
                        price: isTop ? currentPoint.high : currentPoint.low,
                        isTop: isTop
                    });
                }
            }
        }
    }

    // --- New Consolidation Logic ---
    if (reversalPoints.length === 0) {
        return [];
    }

    const consolidatedPoints = [];
    let i = 0;
    const groupWindow = 5; // Max candle distance to group arrows.

    while (i < reversalPoints.length) {
        const currentPoint = reversalPoints[i];
        const currentGroup = [currentPoint];
        let j = i + 1;

        // Group consecutive arrows of the same type that are close to each other
        while (j < reversalPoints.length &&
               reversalPoints[j].isTop === currentPoint.isTop &&
               (reversalPoints[j].index - reversalPoints[j - 1].index) <= groupWindow) {
            currentGroup.push(reversalPoints[j]);
            j++;
        }

        // Consolidate the group into a single point
        if (currentPoint.isTop) {
            // For tops, find the one with the highest price
            consolidatedPoints.push(currentGroup.reduce((prev, curr) => (curr.price > prev.price ? curr : prev)));
        } else {
            // For bottoms, find the one with the lowest price
            consolidatedPoints.push(currentGroup.reduce((prev, curr) => (curr.price < prev.price ? curr : prev)));
        }

        i = j; // Move to the next new group
    }

    return consolidatedPoints;
};

// Function to analyze trading signal and predict future signal ranges
export const analyzeTradingSignal = (data, currentPrice, stockName) => {
    const reversals = findTrendReversalPoints(data);

    // console.log("--- Signal Analysis Debug Info ---");
    // console.log("Current Price:", currentPrice.toFixed(2));
    // console.log("Up Arrows (Bottoms):", reversals.filter(r => !r.isTop).map(r => ({ price: r.price.toFixed(2), index: r.index })));
    // console.log("Down Arrows (Tops):", reversals.filter(r => r.isTop).map(r => ({ price: r.price.toFixed(2), index: r.index })));
    if (currentPrice === null || currentPrice === undefined) {
        console.error(`!!! ERROR: currentPrice is null or undefined inside analyzeTradingSignal for ${stockName || 'unknown stock'} !!!`);
        // Return a neutral signal to prevent crash
        return { signal: 'NEUTRAL', predictedBuyRange: null, predictedSellRange: null };
    }
    // console.log("------------------------------------");

    if (reversals.length === 0) return { signal: 'NEUTRAL', predictedBuyRange: null, predictedSellRange: null };

   

    // Initialize predicted ranges
    let predictedBuyRange = null;
    let predictedSellRange = null;

    // Find potential future buy and sell levels with pattern analysis
    const sortedReversals = [...reversals].sort((a, b) => b.index - a.index); // Most recent first

    // --- BUY RANGE LOGIC ---
    // Find the most recent bottom (up arrow) and peak (down arrow).
    const lastBottom = sortedReversals.find(r => !r.isTop);
    const lastPeak = sortedReversals.find(r => r.isTop);

    // 1. Find the most recent historical bottom that is BELOW the current price.
    // This is our primary candidate for a support level.
    const mostRecentSupport = sortedReversals.find(r => !r.isTop && r.price < currentPrice);

    // 2. If such a support level exists, create a buy range around it.
    if (mostRecentSupport) {
        const mostRelevantBuyLevel = mostRecentSupport.price;
        // The support level is the FLOOR of the buy range.
        // The range extends upwards by a small buffer.
        const buyBuffer = 0.01; // 1% buffer
        predictedBuyRange = {
            min: mostRelevantBuyLevel,
            max: mostRelevantBuyLevel * (1 + buyBuffer)
        };
    }
    
    // --- SELL RANGE LOGIC ---
    // The sell range is determined ONLY by the most recent peak (last "Down Arrow").
    // This is the most relevant resistance level.
    if (lastPeak && lastPeak.price > currentPrice) {
        const nextSellLevel = lastPeak.price;
        
        // The resistance level is the CEILING of the sell range.
        // The range extends downwards by a small buffer.
        const sellBuffer = 0.01; // 1% buffer below for sell range

        predictedSellRange = {
            min: nextSellLevel * (1 - sellBuffer),
            max: nextSellLevel
        };
    }

    // --- Fallback Buy Range Logic ---
    // If no buy range was found (e.g., price is too high for historical support),
    // use the most recent bottom as a fallback to ensure a buy range is always present for context.
    if (!predictedBuyRange && lastBottom) {
        predictedBuyRange = {
            min: lastBottom.price * 0.99,
            max: lastBottom.price * 1.01, // 1% buffer around the last bottom
            isFallback: true // Mark as a fallback range
        };
    }

    // --- Sanity Check ---
    // If the determined buy range (primary or fallback) and sell range overlap, invalidate the buy range.
    if (predictedBuyRange && predictedSellRange && predictedBuyRange.max >= predictedSellRange.min) {
        predictedBuyRange = null;
    }

    // --- Final Profitability Check ---
    // Ensure there's at least a 10% difference between the buy and sell ranges before issuing a signal.
    let isProfitable = true;
    if (predictedBuyRange && predictedSellRange) {
        const potentialProfitMargin = (predictedSellRange.min - predictedBuyRange.max) / predictedBuyRange.max;
        if (potentialProfitMargin < 0.05) {
            isProfitable = false;
        }
    } else {
        // If we don't have both ranges, we can't determine profitability.
        isProfitable = false;
    }

    // --- SIGNAL LOGIC 1: Price is in a predicted range ---
    if (isProfitable && predictedBuyRange && currentPrice >= predictedBuyRange.min && currentPrice <= predictedBuyRange.max) {
        return { signal: 'BUY', predictedBuyRange, predictedSellRange, reason: "Price has entered the predicted buy range." };
    }
    if (predictedSellRange && currentPrice >= predictedSellRange.min && currentPrice <= predictedSellRange.max) {
        // console.log("SELL Signal for", stockName, "- Buy Range:", predictedBuyRange, "Sell Range:", predictedSellRange);
        return { signal: 'SELL', predictedBuyRange, predictedSellRange, reason: "Price has entered the predicted sell range." };
    }

    

    // If no other conditions are met, the signal is NEUTRAL.
    return {
        signal: 'NEUTRAL',
        predictedBuyRange,
        predictedSellRange
    };
};